import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DatePickerProps } from '../../models';
import styles from './datePicker.module.scss';
import IconsColor from '../../util/iconsColor';
import Calendar from '../calendar/Calendar';

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  placeholder = 'DD-MM-YYYY',
  disabled = false,
  required = false,
  error,
  warning,
  width = '100%',
  height = '44px',
  id,
  minDate,
  maxDate,
  leftIcon,
  clearable = false,
}) => {
  const inputId = id || `datepicker-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const hasLeftIcon = !!leftIcon;
  const [showCalendar, setShowCalendar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0, width: 0 });
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // Format date from YYYY-MM-DD to DD-MMM-YYYY (e.g., "12-Aug-2025")
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      // Ensure we have a valid date string in YYYY-MM-DD format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(dateString)) {
        return '';
      }

      const [year, month, day] = dateString.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      // Parse to integers
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      
      // Validate ranges
      if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) {
        return '';
      }
      
      if (monthNum < 1 || monthNum > 12) {
        return '';
      }
      
      if (dayNum < 1 || dayNum > 31) {
        return '';
      }
      
      // Format with leading zero for day if needed
      const dayStr = dayNum < 10 ? `0${dayNum}` : `${dayNum}`;
      const monthStr = monthNames[monthNum - 1];
      
      return `${dayStr}-${monthStr}-${yearNum}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };

  const displayValue = formatDateForDisplay(value);
  const [inputValue, setInputValue] = useState(displayValue);
  const [isEditing, setIsEditing] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  // Sync input value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setInputValue(formatDateForDisplay(value));
      setInternalError(null);
    }
  }, [value, isEditing]);

  // Parse date from various formats to YYYY-MM-DD
  const parseDateInput = (input: string): string | null => {
    if (!input || input.trim() === '') return null;

    const trimmed = input.trim();

    // Priority 1: Try DD-MM-YYYY or DD/MM/YYYY format (e.g., "31-12-2026", "1-1-2026")
    const ddmmyyyyNumericRegex = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;
    const match1 = trimmed.match(ddmmyyyyNumericRegex);
    if (match1) {
      const [, day, month, year] = match1;
      const dayNum = parseInt(day, 10);
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);
      
      // Validate ranges
      if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12 && yearNum >= 1000 && yearNum <= 9999) {
        // Create date to validate (handles invalid dates like 31-02-2026)
        const date = new Date(yearNum, monthNum - 1, dayNum);
        // Check if the date is valid (handles month overflow, leap years, etc.)
        if (
          date.getFullYear() === yearNum &&
          date.getMonth() === monthNum - 1 &&
          date.getDate() === dayNum
        ) {
          return `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        }
      }
    }

    // Priority 2: Try DD-MMM-YYYY format (e.g., "12-Aug-2025")
    const ddmmyyyyRegex = /^(\d{1,2})[-/](\w{3,})[-/](\d{4})$/i;
    const match2 = trimmed.match(ddmmyyyyRegex);
    if (match2) {
      const [, day, monthStr, year] = match2;
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const monthIndex = monthNames.findIndex(m => m === monthStr.toLowerCase());
      if (monthIndex !== -1) {
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(year, 10);
        if (dayNum >= 1 && dayNum <= 31 && yearNum >= 1000 && yearNum <= 9999) {
          const date = new Date(yearNum, monthIndex, dayNum);
          if (date.getFullYear() === yearNum && date.getMonth() === monthIndex && date.getDate() === dayNum) {
            return `${yearNum}-${String(monthIndex + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          }
        }
      }
    }

    // Try YYYY-MM-DD format
    const yyyymmddRegex = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;
    const match3 = trimmed.match(yyyymmddRegex);
    if (match3) {
      const [, year, month, day] = match3;
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12 && yearNum >= 1000 && yearNum <= 9999) {
        const date = new Date(yearNum, monthNum - 1, dayNum);
        if (date.getFullYear() === yearNum && date.getMonth() === monthNum - 1 && date.getDate() === dayNum) {
          return `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        }
      }
    }

    return null;
  };

  // Validate date against min/max constraints
  const validateDate = (dateString: string): boolean => {
    if (!dateString) return true; // Empty is valid

    if (minDate && dateString < minDate) return false;
    if (maxDate && dateString > maxDate) return false;

    return true;
  };

  // Apply input mask for DD-MM-YYYY format
  const applyDateMask = (value: string, previousValue: string = ''): { masked: string; cursorPos: number } => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 8 digits (DDMMYYYY)
    const limitedDigits = digitsOnly.slice(0, 8);
    
    // Apply mask: DD-MM-YYYY
    let maskedValue = '';
    for (let i = 0; i < limitedDigits.length; i++) {
      if (i === 2 || i === 4) {
        maskedValue += '-';
      }
      maskedValue += limitedDigits[i];
    }
    
    // Calculate cursor position
    const prevDigits = previousValue.replace(/\D/g, '').length;
    const newDigits = limitedDigits.length;
    const isDeleting = newDigits < prevDigits;
    
    let cursorPos = maskedValue.length;
    if (!isDeleting && newDigits > 0) {
      // If adding digits, position cursor after the last digit
      if (newDigits <= 2) {
        cursorPos = newDigits;
      } else if (newDigits <= 4) {
        cursorPos = newDigits + 1; // +1 for first dash
      } else {
        cursorPos = newDigits + 2; // +2 for both dashes
      }
      // Don't exceed max length
      cursorPos = Math.min(cursorPos, 10);
    }
    
    return { masked: maskedValue, cursorPos };
  };

  // Handle manual input change with mask
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const previousValue = inputValue;
    
    // Apply mask
    const { masked, cursorPos } = applyDateMask(newValue, previousValue);
    
    setInputValue(masked);
    setIsEditing(true);
    setInternalError(null);
    
    // Adjust cursor position after mask is applied
    setTimeout(() => {
      const input = e.target;
      input.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  // Handle input blur - validate and update
  const handleInputBlur = () => {
    setIsEditing(false);
    const trimmedValue = inputValue.trim();
    
    // If empty, clear the value
    if (trimmedValue === '') {
      onChange('');
      setInputValue('');
      setInternalError(null);
      return;
    }

    // Try to parse the input
    const parsedDate = parseDateInput(trimmedValue);
    
    if (parsedDate && validateDate(parsedDate)) {
      // Valid date - update value
      onChange(parsedDate);
      setInputValue(formatDateForDisplay(parsedDate));
      setInternalError(null);
    } else {
      // Invalid input - revert to last valid value and show error
      setInternalError('Invalid date format. Please use DD-MM-YYYY format (e.g., 31-12-2026)');
      if (value) {
        setInputValue(formatDateForDisplay(value));
      } else {
        setInputValue('');
      }
    }
  };

  // Handle key down events
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, and arrow keys
    if (
      ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)
    ) {
      return;
    }
    
    // Allow Ctrl/Cmd + A, C, V, X
    if (e.ctrlKey || e.metaKey) {
      if (['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
        return;
      }
    }
    
    // Only allow digits
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
      return;
    }
    
    // Prevent typing if we've reached max length (10 characters: DD-MM-YYYY)
    const currentValue = (e.target as HTMLInputElement).value;
    const digitsOnly = currentValue.replace(/\D/g, '');
    if (digitsOnly.length >= 8) {
      e.preventDefault();
      return;
    }
    
    // Handle Enter key
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  // Handle paste events
  const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    // Extract digits from pasted text
    const digitsOnly = pastedText.replace(/\D/g, '').slice(0, 8);
    
    if (digitsOnly.length > 0) {
      // Apply mask to pasted content
      const { masked } = applyDateMask(digitsOnly);
      setInputValue(masked);
      setIsEditing(true);
      setInternalError(null);
      
      // Set cursor to end
      setTimeout(() => {
        const input = e.currentTarget;
        input.setSelectionRange(masked.length, masked.length);
      }, 0);
    }
  };

  // Handle date change from calendar
  const handleDateChange = (dateString: string) => {
    onChange(dateString);
    setShowCalendar(false);
    setIsEditing(false);
  };

  // Handle clear button click
  const handleClear = () => {
    onChange('');
  };

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Calculate position (above or below) when calendar opens
  const calculatePosition = useCallback(() => {
    if (!inputContainerRef.current || isMobile) return;

    const inputRect = inputContainerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;
    const calendarHeight = 400; // Approximate calendar height

    // If not enough space below but more space above, open above
    if (spaceBelow < calendarHeight && spaceAbove > spaceBelow) {
      setOpenAbove(true);
      setCalendarPosition({
        top: inputRect.top - calendarHeight - 8,
        left: inputRect.left,
        width: inputRect.width
      });
    } else {
      setOpenAbove(false);
      setCalendarPosition({
        top: inputRect.bottom + 8,
        left: inputRect.left,
        width: inputRect.width
      });
    }
  }, [isMobile]);

  // Handle click on display input to show calendar
  const handleDisplayClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!disabled) {
      // If input already has focus (user is typing), don't toggle calendar
      if (document.activeElement === e.currentTarget) {
        return;
      }
      // Otherwise, toggle calendar
      if (!showCalendar) {
        calculatePosition();
        setShowCalendar(true);
      } else {
        setShowCalendar(false);
      }
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsEditing(true);
    // Close calendar when user starts typing
    if (showCalendar) {
      setShowCalendar(false);
    }
  };

  // Recalculate position on scroll or resize
  useEffect(() => {
    if (!showCalendar) return;

    const handleScrollOrResize = () => {
      calculatePosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showCalendar, isMobile, calculatePosition]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showCalendar &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        inputContainerRef.current &&
        !inputContainerRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  // Prevent body scroll when calendar is open on mobile
  useEffect(() => {
    if (showCalendar && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showCalendar, isMobile]);

  // Handle ESC key to close calendar
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showCalendar) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showCalendar]);

  return (
    <div className={styles.datePickerWrapper} style={{ width }}>
      <label htmlFor={inputId} className={styles.datePickerLabel}>
        {label}
        {label?.length > 0 && required && <span className={styles.requiredIndicator}>*</span>}
      </label>

      <div className={styles.datePickerContainer} ref={inputContainerRef}>
        {leftIcon && (
          <div className={styles.datePickerIcon + ' ' + styles.datePickerIconLeft}>
            {leftIcon}
          </div>
        )}

        {/* Display input - shows formatted date, editable with mask */}
        <input
          type="text"
          id={inputId}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          onPaste={handleInputPaste}
          onClick={handleDisplayClick}
          autoComplete="off"
          className={
            styles.datePickerField +
            ' ' +
            (error || internalError ? styles.error : '') +
            ' ' +
            (warning && !error && !internalError ? styles.warning : '') +
            ' ' +
            (disabled ? styles.disabled : '')
          }
          style={{
            height,
            paddingLeft: hasLeftIcon ? '48px' : '16px',
            paddingRight: '48px', // Always leave space for calendar or clear icon
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />

        {/* Calendar icon */}
        {(!clearable || !value) && !disabled && (
          <div className={styles.calendarIconButton} onClick={handleDisplayClick}>
            <IconsColor
              iconName="calendarIcon"
              width="20px"
              height="20px"
              fillColor="none"
              strokeColor="currentColor"
            />
          </div>
        )}

        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="Clear date"
            title="Clear date"
          >
            <IconsColor
              iconName="closeIcon"
              width="16px"
              height="16px"
              fillColor="none"
              strokeColor="currentColor"
            />
          </button>
        )}

        {/* Calendar Popup - Using Portal to render outside DOM hierarchy */}
        {showCalendar && createPortal(
          <>
            {isMobile ? (
              /* Mobile: Show as centered modal with backdrop */
              <div className={styles.calendarBackdrop} onClick={() => setShowCalendar(false)}>
                <div className={styles.calendarModal} ref={calendarRef} onClick={(e) => e.stopPropagation()}>
                  <Calendar
                    selectedDate={value}
                    onChange={handleDateChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    onClose={() => setShowCalendar(false)}
                  />
                </div>
              </div>
            ) : (
              /* Desktop: Show as fixed positioned dropdown (above or below) */
              <div 
                className={`${styles.calendarPopup} ${openAbove ? styles.calendarAbove : styles.calendarBelow}`} 
                ref={calendarRef}
                style={{
                  position: 'fixed',
                  top: `${calendarPosition.top}px`,
                  left: `${calendarPosition.left}px`,
                  minWidth: `${calendarPosition.width}px`
                }}
              >
                <Calendar
                  selectedDate={value}
                  onChange={handleDateChange}
                  minDate={minDate}
                  maxDate={maxDate}
                  onClose={() => setShowCalendar(false)}
                />
              </div>
            )}
          </>,
          document.body
        )}
      </div>

      {(error || internalError) && (
        <span className={styles.message + ' ' + styles.errorMessage}>
          <IconsColor
            iconName="errorIcon"
            width="16px"
            height="16px"
            fillColor="none"
            strokeColor="currentColor"
          />
          {error || internalError}
        </span>
      )}

      {warning && !error && !internalError && (
        <span className={styles.message + ' ' + styles.warningMessage}>
          <IconsColor
            iconName="warningIcon"
            width="16px"
            height="16px"
            fillColor="none"
            strokeColor="currentColor"
          />
          {warning}
        </span>
      )}
    </div>
  );
};

export default DatePicker;

